import './styles.css';
import { apiRequest, ApiError } from './api.js';
import {
  activeRouteStep,
  arrivalRadius,
  bearingDegrees,
  buildFallbackRoute,
  closestRouteIndex,
  distanceToRouteMetres,
  finalApproachThreshold,
  formatDistance,
  haversineMetres,
  smoothAngle,
} from './geo.js';

const STORAGE_KEY = 'cacheline_device_v1';
const CACHE_KEY = 'cacheline_cache_snapshot_v1';
const demoMode = new URLSearchParams(location.search).get('demo');

const state = {
  screen: 'pair',
  token: localStorage.getItem(STORAGE_KEY) || '',
  caches: loadJson(CACHE_KEY)?.caches || [],
  selectedIndex: 0,
  location: null,
  heading: 0,
  smoothedHeading: NaN,
  route: null,
  routeLoading: false,
  routingAvailable: true,
  rerouteAt: 0,
  watchId: null,
  orientationActive: false,
  arrivalSamples: 0,
  toastTimer: null,
};

const app = document.getElementById('app');
let map = null;
let mapTools = null;
let mapLoadPromise = null;
let lastMapHeadingRenderAt = 0;

document.addEventListener('keydown', handleKeydown, { passive: false });
document.addEventListener('click', handleClick);
document.addEventListener('visibilitychange', handleVisibility);

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/service-worker.js', { scope: '/app/' }).catch(() => {});
}

boot();

async function boot() {
  if (!state.token) {
    showScreen('pair');
    return;
  }
  showScreen('loading', false);
  await syncCaches({ quiet: true });
}

function loadJson(key) {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
}

function showScreen(name, focus = true) {
  state.screen = name;
  document.querySelectorAll('[data-screen]').forEach((screen) => {
    screen.classList.toggle('hidden', screen.dataset.screen !== name);
  });
  app.classList.toggle('map-visible', name === 'explore' || name === 'route');
  document.getElementById('map-shell').setAttribute('aria-hidden', String(!(name === 'explore' || name === 'route')));
  if (name === 'explore') renderExplore();
  if (name === 'details') renderDetails();
  if (name === 'route' || name === 'stealth') renderNavigation();
  if (focus) requestAnimationFrame(() => focusFirst(name));
}

function focusFirst(screenName) {
  const preferred = screenName === 'explore' ? '[data-action="navigate"]' : '.focusable';
  const element = document.querySelector(`#screen-${screenName} ${preferred}`) || document.querySelector(`#screen-${screenName} .focusable`);
  element?.focus();
}

function currentCache() {
  return state.caches[state.selectedIndex] || null;
}

function renderExplore() {
  const cache = currentCache();
  if (!cache) return;
  document.getElementById('cache-index').textContent = `${state.selectedIndex + 1} / ${state.caches.length}`;
  document.getElementById('cache-code').textContent = `${cache.externalCode || 'GPX'} · ${cache.type || 'cache'}`.toUpperCase();
  document.getElementById('cache-name').textContent = cache.name;
  document.getElementById('cache-distance').textContent = state.location ? formatDistance(haversineMetres(state.location, cache)) : 'GPS —';
  if (state.screen === 'explore') renderMap();
}

function renderDetails() {
  const cache = currentCache();
  if (!cache) return;
  document.getElementById('detail-code').textContent = `${cache.externalCode || 'GPX'} · PRIVATE IMPORT`.toUpperCase();
  document.getElementById('detail-name').textContent = cache.name;
  document.getElementById('detail-type').textContent = titleCase(cache.type || 'traditional');
  document.getElementById('detail-size').textContent = cache.size || 'Not listed';
  document.getElementById('detail-difficulty').textContent = cache.difficulty ? `${cache.difficulty} / 5` : 'Not listed';
  document.getElementById('detail-terrain').textContent = cache.terrain ? `${cache.terrain} / 5` : 'Not listed';
  document.getElementById('detail-hint').textContent = cache.hint || cache.description || 'No hint included in this GPX.';
  document.getElementById('detail-type-mark').textContent = cache.type === 'multi' ? '◇' : cache.type === 'mystery' ? '?' : '○';
}

function renderMap() {
  if (state.screen !== 'explore' && state.screen !== 'route') return;
  if (!map) {
    ensureMap();
    return;
  }
  if (!map.getSource('caches')) return;
  const cache = currentCache();
  mapTools.setCaches(map, state.caches, cache?.id);
  mapTools.setPosition(map, state.location, state.heading);
  mapTools.setRoute(map, state.route?.geometry || null);
  if (state.screen === 'explore') mapTools.fitCaches(map, state.caches);
  if (state.screen === 'route' && state.location && cache) mapTools.fitNavigation(map, state.location, cache);
}

function ensureMap() {
  if (mapLoadPromise) return mapLoadPromise;
  mapLoadPromise = import('./map.js').then((module) => {
    mapTools = module;
    map = module.createCacheMap('map');
    map.on('load', renderMap);
    return map;
  }).catch((error) => {
    mapLoadPromise = null;
    showToast('The map could not be loaded. Navigation HUD is still available.');
    console.error(error);
    return null;
  });
  return mapLoadPromise;
}

function handleClick(event) {
  const digit = event.target.closest('[data-digit]');
  if (digit) {
    changeDigit(digit, 1);
    return;
  }
  const action = event.target.closest('[data-action]')?.dataset.action;
  if (!action) return;
  runAction(action);
}

function runAction(action) {
  switch (action) {
    case 'claim-pairing': claimPairing(); break;
    case 'sync': syncCaches(); break;
    case 'disconnect': disconnect(); break;
    case 'previous-cache': selectCache(-1); break;
    case 'next-cache': selectCache(1); break;
    case 'details': showScreen('details'); break;
    case 'back-explore': showScreen('explore'); break;
    case 'navigate': startNavigation(); break;
    case 'stealth': showScreen('stealth'); break;
    case 'show-route': showScreen('route'); renderMap(); break;
    case 'stop-route': stopNavigation(); break;
  }
}

async function claimPairing() {
  const pin = Array.from(document.querySelectorAll('[data-digit]')).map((digit) => digit.textContent.trim().at(-1)).join('');
  try {
    showScreen('loading', false);
    const result = await apiRequest('pairings/claim', { body: { pin } });
    state.token = result.deviceToken;
    localStorage.setItem(STORAGE_KEY, state.token);
    await syncCaches({ quiet: true });
    showToast('Library synced. This PIN can be used again anytime.');
  } catch (error) {
    showScreen('pair');
    showToast(error.message || 'The library PIN could not be opened.');
  }
}

async function syncCaches({ quiet = false } = {}) {
  if (!state.token) return;
  const selectedCode = currentCache()?.externalCode;
  if (!quiet) showToast('Syncing library…');
  try {
    const result = await apiRequest('caches', { token: state.token });
    state.caches = result.caches || [];
    state.selectedIndex = Math.max(0, state.caches.findIndex((cache) => cache.externalCode === selectedCode));
    localStorage.setItem(CACHE_KEY, JSON.stringify({ caches: state.caches, syncedAt: result.syncedAt }));
    if (state.caches.length) {
      showScreen(state.screen === 'details' ? 'details' : 'explore');
      renderMap();
    } else {
      showScreen('empty');
    }
    if (!quiet) showToast(`${state.caches.length} cache${state.caches.length === 1 ? '' : 's'} synced.`);
  } catch (error) {
    if (error.status === 401) {
      disconnect(false);
      showToast('Library access changed. Enter the PIN again.');
    } else if (state.caches.length) {
      showScreen('explore');
      showToast('Offline — showing the last synced library.');
    } else {
      showScreen('empty');
      showToast(error.message || 'Library sync failed.');
    }
  }
}

function disconnect(showMessage = true) {
  stopSensors();
  state.token = '';
  state.caches = [];
  state.route = null;
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(CACHE_KEY);
  document.querySelectorAll('[data-digit]').forEach((digit) => { digit.textContent = '0'; });
  showScreen('pair');
  if (showMessage) showToast('Library disconnected.');
}

function selectCache(delta) {
  if (!state.caches.length) return;
  state.selectedIndex = (state.selectedIndex + delta + state.caches.length) % state.caches.length;
  renderExplore();
}

async function startNavigation() {
  const cache = currentCache();
  if (!cache || state.routeLoading) return;
  state.routeLoading = true;
  state.route = null;
  state.arrivalSamples = 0;
  showScreen('route');
  document.getElementById('route-instruction').textContent = 'Getting your position…';
  try {
    await startOrientation();
    const location = demoMode ? demoLocation(cache, demoMode) : await getCurrentLocation();
    handleLocation(location);
    if (demoMode === 'arrive') state.arrivalSamples = 3;
    await requestRoute({ quiet: false });
    startLocationWatch();
  } catch (error) {
    showToast(error.message || 'Location is unavailable.');
    stopNavigation();
  } finally {
    state.routeLoading = false;
  }
}

async function requestRoute({ quiet }) {
  const cache = currentCache();
  if (!cache || !state.location) return;
  const origin = [state.location.longitude, state.location.latitude];
  const destination = [cache.longitude, cache.latitude];
  let route;
  try {
    route = await apiRequest('route', { token: state.token, body: { origin, destination } });
    state.routingAvailable = true;
  } catch (error) {
    route = buildFallbackRoute(origin, destination);
    if (error instanceof ApiError && error.code === 'routing_unconfigured') state.routingAvailable = false;
    if (!quiet) showToast(state.routingAvailable ? 'Route unavailable — showing bearing preview.' : 'Add ORS_API_KEY for trail-aware turns.');
  }
  state.route = route;
  state.rerouteAt = Date.now();
  renderNavigation();
  renderMap();
}

function getCurrentLocation() {
  if (!navigator.geolocation) return Promise.reject(new Error('This browser has no location support.'));
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(positionToLocation(position)),
      () => reject(new Error('Allow location access to begin navigation.')),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 2000 },
    );
  });
}

function startLocationWatch() {
  if (demoMode || !navigator.geolocation || state.watchId !== null) return;
  state.watchId = navigator.geolocation.watchPosition(
    (position) => handleLocation(positionToLocation(position)),
    () => showToast('GPS signal lost — holding the last route.'),
    { enableHighAccuracy: true, timeout: 20000, maximumAge: 1500 },
  );
}

function positionToLocation(position) {
  return {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy: position.coords.accuracy || 20,
    heading: position.coords.heading,
    speed: position.coords.speed,
    timestamp: position.timestamp || Date.now(),
  };
}

function demoLocation(cache, mode) {
  if (mode === 'arrive') {
    return { latitude: cache.latitude + 0.000015, longitude: cache.longitude - 0.000012, accuracy: 14, heading: 30, speed: 0, timestamp: Date.now() };
  }
  return { latitude: cache.latitude - 0.0032, longitude: cache.longitude - 0.0044, accuracy: 16, heading: 35, speed: 1.2, timestamp: Date.now() };
}

function handleLocation(location) {
  state.location = location;
  if (Number.isFinite(location.heading) && location.speed > 0.8) {
    state.smoothedHeading = smoothAngle(state.smoothedHeading, location.heading);
    state.heading = state.smoothedHeading;
  }
  const cache = currentCache();
  if (cache) {
    const distance = haversineMetres(location, cache);
    state.arrivalSamples = distance <= arrivalRadius(location.accuracy) ? state.arrivalSamples + 1 : 0;
  }
  renderExplore();
  renderNavigation();
  renderMap();
  maybeReroute();
}

function maybeReroute() {
  if (!state.route || !state.routingAvailable || state.routeLoading || Date.now() - state.rerouteAt < 25000) return;
  const threshold = Math.min(80, Math.max(25, (state.location?.accuracy || 20) * 1.5));
  if (distanceToRouteMetres(state.location, state.route.geometry) <= threshold) return;
  state.routeLoading = true;
  requestRoute({ quiet: true }).finally(() => {
    state.routeLoading = false;
    const status = document.getElementById('route-status');
    status.textContent = 'Route adjusted';
    setTimeout(() => renderNavigation(), 1800);
  });
}

async function startOrientation() {
  if (state.orientationActive || typeof DeviceOrientationEvent === 'undefined') return;
  if (typeof DeviceOrientationEvent.requestPermission === 'function') {
    const permission = await DeviceOrientationEvent.requestPermission();
    if (permission !== 'granted') return;
  }
  window.addEventListener('deviceorientation', handleOrientation);
  state.orientationActive = true;
}

function handleOrientation(event) {
  const compassHeading = Number.isFinite(event.webkitCompassHeading) ? event.webkitCompassHeading : event.alpha;
  if (!Number.isFinite(compassHeading)) return;
  state.smoothedHeading = smoothAngle(state.smoothedHeading, compassHeading);
  state.heading = state.smoothedHeading;
  if (state.screen === 'route' || state.screen === 'stealth') {
    renderNavigation();
    renderMapHeading();
  }
}

function renderMapHeading() {
  if (state.screen !== 'route' || !map || !mapTools || !state.location) return;
  const now = performance.now();
  if (now - lastMapHeadingRenderAt < 66) return;
  lastMapHeadingRenderAt = now;
  mapTools.setPosition(map, state.location, state.heading);
}

function renderNavigation() {
  if (!state.location || !state.route || !currentCache()) return;
  const cache = currentCache();
  const distanceToCache = haversineMetres(state.location, cache);
  const routeIndex = closestRouteIndex(state.location, state.route.geometry);
  const step = activeRouteStep(state.route.steps, routeIndex) || { instruction: 'Continue toward the cache', wayPoints: [routeIndex, routeIndex] };
  const lastStep = step === state.route.steps.at(-1);
  const finalMode = distanceToCache <= finalApproachThreshold(state.location.accuracy) || lastStep;
  const arrived = state.arrivalSamples >= 3;
  const targetCoordinate = finalMode
    ? [cache.longitude, cache.latitude]
    : state.route.geometry[Math.min(step.wayPoints?.[1] ?? routeIndex + 1, state.route.geometry.length - 1)];
  const target = { longitude: targetCoordinate[0], latitude: targetCoordinate[1] };
  const targetDistance = finalMode ? distanceToCache : haversineMetres(state.location, target);
  const relativeBearing = bearingDegrees(state.location, target) - state.heading;
  const instruction = arrived ? 'You’re here' : finalMode ? 'Leave the path and follow the arrow' : step.instruction;
  const subtitle = arrived
    ? `Search inside the ${Math.round(arrivalRadius(state.location.accuracy))} m GPS area`
    : finalMode ? `${cache.name} · final approach` : `${cache.name} · ${formatDistance(distanceToCache)} remaining`;
  const accuracyText = `GPS ±${Math.round(state.location.accuracy || 0)} m`;

  document.getElementById('route-instruction').textContent = instruction;
  document.getElementById('route-distance').textContent = finalMode ? `FINAL · ${formatDistance(distanceToCache)}` : `${formatDistance(targetDistance)} · NEXT`;
  document.getElementById('route-symbol').textContent = maneuverSymbol(step.type, finalMode);
  document.getElementById('route-status').textContent = `${accuracyText}${state.route.fallback ? ' · bearing preview' : ''}`;
  document.getElementById('stealth-code').textContent = arrived ? 'SEARCH AREA' : finalMode ? 'FINAL APPROACH' : 'ON ROUTE';
  document.getElementById('stealth-accuracy').textContent = accuracyText;
  document.getElementById('stealth-distance').textContent = formatDistance(targetDistance);
  document.getElementById('stealth-instruction').textContent = instruction;
  document.getElementById('stealth-subtitle').textContent = subtitle;
  document.getElementById('bearing-arrow').style.transform = `rotate(${relativeBearing}deg)`;
  document.getElementById('maneuver-panel').classList.toggle('arrived', arrived);
}

function maneuverSymbol(type, finalMode) {
  if (finalMode) return '△';
  if ([0, 1, 2].includes(type)) return '←';
  if ([3, 4, 5].includes(type)) return '→';
  if (type === 12) return '↻';
  return '↑';
}

function stopNavigation() {
  stopSensors();
  state.route = null;
  state.routeLoading = false;
  state.arrivalSamples = 0;
  if (map && mapTools) mapTools.setRoute(map, null);
  showScreen(state.caches.length ? 'explore' : 'empty');
}

function stopSensors() {
  if (state.watchId !== null && navigator.geolocation) navigator.geolocation.clearWatch(state.watchId);
  state.watchId = null;
  if (state.orientationActive) window.removeEventListener('deviceorientation', handleOrientation);
  state.orientationActive = false;
}

function handleVisibility() {
  if (document.hidden) stopSensors();
  else if (state.route && !demoMode) {
    startOrientation().catch(() => {});
    startLocationWatch();
  }
}

function handleKeydown(event) {
  if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape'].includes(event.key)) return;
  const focusedDigit = document.activeElement?.matches?.('[data-digit]') ? document.activeElement : null;
  if (focusedDigit && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
    changeDigit(focusedDigit, event.key === 'ArrowUp' ? 1 : -1);
  } else if (event.key === 'Enter') {
    if (focusedDigit) {
      const index = Number(focusedDigit.dataset.digit);
      const next = document.querySelector(`[data-digit="${index + 1}"]`) || document.querySelector('[data-action="claim-pairing"]');
      next?.focus();
    } else {
      document.activeElement?.click();
    }
  } else if (event.key === 'Escape') {
    if (state.screen === 'details') showScreen('explore');
    else if (state.screen === 'route' || state.screen === 'stealth') stopNavigation();
  } else {
    moveFocus(event.key);
  }
  event.preventDefault();
}

function changeDigit(element, delta) {
  const value = Number(element.textContent.trim().at(-1));
  element.textContent = String((value + delta + 10) % 10);
  element.setAttribute('aria-label', `Library PIN digit ${Number(element.dataset.digit) + 1}: ${element.textContent}`);
}

function moveFocus(key) {
  const screen = document.getElementById(`screen-${state.screen}`);
  const candidates = Array.from(screen?.querySelectorAll('.focusable:not([disabled])') || []).filter((element) => element.offsetParent !== null);
  if (!candidates.length) return;
  const current = candidates.includes(document.activeElement) ? document.activeElement : candidates[0];
  if (state.screen === 'pair' && current.matches('[data-digit]') && (key === 'ArrowLeft' || key === 'ArrowRight')) {
    const currentIndex = Number(current.dataset.digit);
    if (key === 'ArrowRight' && currentIndex === 5) {
      document.querySelector('[data-action="claim-pairing"]')?.focus();
      return;
    }
    const index = currentIndex + (key === 'ArrowLeft' ? -1 : 1);
    (document.querySelector(`[data-digit="${index}"]`) || current).focus();
    return;
  }
  if (state.screen === 'pair' && current.matches('[data-action="claim-pairing"]') && (key === 'ArrowLeft' || key === 'ArrowRight')) {
    document.querySelector(`[data-digit="${key === 'ArrowLeft' ? 5 : 0}"]`)?.focus();
    return;
  }
  const origin = centerOf(current);
  const direction = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] }[key];
  const ranked = candidates.filter((candidate) => candidate !== current).map((candidate) => {
    const point = centerOf(candidate);
    const dx = point.x - origin.x;
    const dy = point.y - origin.y;
    const forward = dx * direction[0] + dy * direction[1];
    const cross = Math.abs(dx * direction[1] - dy * direction[0]);
    return { candidate, forward, score: forward + cross * 1.8 };
  }).filter((item) => item.forward > 2).sort((a, b) => a.score - b.score);
  (ranked[0]?.candidate || current).focus();
}

function centerOf(element) {
  const rect = element.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

function titleCase(value) {
  return String(value).replace(/[-_]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function showToast(message) {
  const toast = document.getElementById('toast');
  clearTimeout(state.toastTimer);
  toast.textContent = message;
  toast.classList.remove('hidden');
  const duration = Math.min(8000, 3500 + Math.max(0, message.split(/\s+/).length - 2) * 300);
  state.toastTimer = setTimeout(() => toast.classList.add('hidden'), duration);
}

if (demoMode) {
  window.__CACHELINE_TEST__ = {
    setLocation(latitude, longitude, accuracy = 15, heading = 0) {
      handleLocation({ latitude, longitude, accuracy, heading, speed: 0, timestamp: Date.now() });
    },
    state,
  };
}
